import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import browserHistory from '../../lib/history.js';
import MemoizedProductListRow from '../products/memoized-product-list-row.js';
import {
  doGetPopularCategoryProducts,
  doGetCategoryProducts,
} from '../../actions/product-category-actions.js';
import { SEE_ALL_CATEGORY_THRESHOLD } from '../../strings.js';
import {
  filterInLimitedCatalog,
  mergePopularProducts,
} from '../../helpers/product-helpers.js';
import { mapLocationParentCategoriesIds } from '../../helpers/product-category-helpers.js';
import './officeluv-catalog.css';

export class OfficeluvFullCatalog extends React.PureComponent {
  constructor(props) {
    super(props);

    this._requestCache = new Set();

    this.loadProductsProgressively(props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isRequesting) return;

    this.loadProductsProgressively(nextProps);
  }

  getCacheKey(categoryId, type) {
    return `${categoryId}-${type}`;
  }

  isRequestInFlight(categoryId, type) {
    return this._requestCache.has(this.getCacheKey(categoryId, type));
  }

  markRequestStarted(categoryId, type) {
    this._requestCache.add(this.getCacheKey(categoryId, type));
  }

  markRequestCompleted(categoryId, type) {
    this._requestCache.delete(this.getCacheKey(categoryId, type));
  }

  loadProductsProgressively(props) {
    // Phase 1: Load popular products immediately for visible categories
    const visibleCategories = props.categories.slice(0, 6); // Load first 6 categories immediately
    visibleCategories.forEach((category, index) => {
      if (!category.popular_products && !this.isRequestInFlight(category.id, 'popular')) {
        this.markRequestStarted(category.id, 'popular');
        props.actions.doGetPopularCategoryProducts(category, '', false)
          .finally(() => this.markRequestCompleted(category.id, 'popular'));
      }
    });

    // Phase 2: Load remaining categories with delay to avoid overwhelming the browser
    const remainingCategories = props.categories.slice(6);
    remainingCategories.forEach((category, index) => {
      setTimeout(() => {
        if (!category.popular_products && !this.isRequestInFlight(category.id, 'popular')) {
          this.markRequestStarted(category.id, 'popular');
          props.actions.doGetPopularCategoryProducts(category, '', false)
            .finally(() => this.markRequestCompleted(category.id, 'popular'));
        }
      }, index * 100); // Stagger requests by 100ms
    });

    // Phase 3: Load full product lists only when needed (lazy loading)
    props.categories.forEach((category) => {
      if (category.popular_products && category.popular_products.length < 7 && !category.products) {
        // Only load if user might need to see "See All" - delay this
        setTimeout(() => {
          if (!this.isRequestInFlight(category.id, 'products')) {
            this.markRequestStarted(category.id, 'products');
            props.actions.doGetCategoryProducts(category, '', false)
              .finally(() => this.markRequestCompleted(category.id, 'products'));
          }
        }, 2000); // Wait 2 seconds before loading full product lists
      }
    });
  }

  _handleClickViewAll = (category) => {
    browserHistory.push('/supplies/catalog/' + category.id);
  };

  renderItem = (item) => {
    let products = mergePopularProducts(item.popular_products, item.products);

    if (this.props.inLimitedViewingMode) {
      products = filterInLimitedCatalog(products, this.props.inCatalogCache);
    }

    if (!products || products.length === 0) return null;

    return (
      <div key={item.id} className="category-catalog-all-row">
        <div className="category-catalog-row-header">
          <h4>Top {item.name}</h4>
          {products.length > SEE_ALL_CATEGORY_THRESHOLD && (
            <Link to={'/supplies/catalog/' + item.id}>See All</Link>
          )}
        </div>

        <MemoizedProductListRow
          items={products}
          onClickViewAll={this._handleClickViewAll.bind(this, item)}
          maxDisplay={SEE_ALL_CATEGORY_THRESHOLD}
        />
      </div>
    );
  };

  render() {
    return (
      <div className="catalog-all">
        {this.props.categories.map(this.renderItem)}
      </div>
    );
  }
}

OfficeluvFullCatalog.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      popular_products: PropTypes.array,
      location_popular_products: PropTypes.array,
    })
  ).isRequired,
  actions: PropTypes.shape({
    doGetPopularCategoryProducts: PropTypes.func.isRequired,
  }).isRequired,
  inLimitedViewingMode: PropTypes.bool.isRequired,
  isRequesting: PropTypes.bool.isRequired,
  inCatalogCache: PropTypes.array.isRequired,
};

function filterParents(c) {
  return !c.parent_id;
}

function filterEnabledCategories(locationParentCategories, category) {
  return locationParentCategories.includes(category.id);
}

function sortByOrder(a, b) {
  if (!a.order) return 1;
  if (a.order < b.order) return -1;
  if (a.order > b.order) return 1;
}

export function mapStateToProps(state) {
  const locationParentCategories = mapLocationParentCategoriesIds(
    state.locationParentProductCategories.items
  );

  return {
    categories: state.productCategories.items
      .filter((item) => filterEnabledCategories(locationParentCategories, item))
      .filter(filterParents)
      .sort(sortByOrder),
    inLimitedViewingMode: state.application.inLimitedViewingMode,
    isRequesting: !!state.productCategories.requesting.length,
    inCatalogCache: state.products.in_catalog,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        doGetPopularCategoryProducts,
        doGetCategoryProducts,
      },
      dispatch
    ),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.productCategories.items === next.productCategories.items &&
    prev.locationParentProductCategories.items ===
      next.locationParentProductCategories.items &&
    prev.application.inLimitedViewingMode ===
      next.application.inLimitedViewingMode &&
    prev.productCategories.requesting === next.productCategories.requesting &&
    prev.products.in_catalog === next.products.in_catalog
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(OfficeluvFullCatalog);
